import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Badge } from '@communitly/shared';
import { useAuth } from '@/contexts/AuthContext';

// Get business badges (badges assigned to a specific business)
export const useBusinessBadges = (businessId: string) => {
  return useQuery({
    queryKey: ['business-badges', businessId],
    queryFn: async () => {
      if (!businessId) return [];

      const { data, error } = await supabase
        .from('business_badges')
        .select(`
          badge_id,
          assigned_at,
          badges:badge_id (
            id,
            name,
            slug,
            description,
            color,
            icon,
            is_active,
            is_system_badge,
            translation_key
          )
        `)
        .eq('business_id', businessId);

      if (error) throw error;

      // Transform the data to return badge objects with assignment info
      return data?.map(item => ({
        ...item.badges,
        assigned_at: item.assigned_at
      })).filter(badge => badge.id) as (Badge & { assigned_at: string })[];
    },
    enabled: !!businessId,
  });
};

// Get all badge assignments for admin overview
export const useAllBadgeAssignments = (communityId: string) => {
  return useQuery({
    queryKey: ['all-badge-assignments', communityId],
    queryFn: async () => {
      if (!communityId) return [];

      const { data, error } = await supabase
        .from('business_badges')
        .select(`
          business_id,
          badge_id,
          assigned_at,
          businesses:business_id (
            id,
            business_name,
            community_id
          ),
          badges:badge_id (
            id,
            name,
            slug,
            description,
            color,
            icon,
            is_active,
            is_system_badge,
            translation_key
          )
        `)
        .eq('businesses.community_id', communityId);

      if (error) throw error;

      return data?.filter(item => item.businesses && item.badges) || [];
    },
    enabled: !!communityId,
  });
};

// Assign badge to business
export const useAssignBadge = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, badgeId }: { businessId: string; badgeId: string }) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Check if the assignment already exists
      const { data: existing } = await supabase
        .from('business_badges')
        .select('id')
        .eq('business_id', businessId)
        .eq('badge_id', badgeId)
        .single();

      if (existing) {
        throw new Error('Badge is already assigned to this business');
      }

      const { error } = await supabase
        .from('business_badges')
        .insert({
          business_id: businessId,
          badge_id: badgeId,
          assigned_at: new Date().toISOString(),
          assigned_by: user.id
        });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['business-badges', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['all-badge-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      toast({
        title: "Success",
        description: "Badge assigned successfully",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to assign badge",
      });
    },
  });
};

// Unassign badge from business
export const useUnassignBadge = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, badgeId }: { businessId: string; badgeId: string }) => {
      const { error } = await supabase
        .from('business_badges')
        .delete()
        .eq('business_id', businessId)
        .eq('badge_id', badgeId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['business-badges', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['all-badge-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      toast({
        title: "Success",
        description: "Badge unassigned successfully",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to unassign badge",
      });
    },
  });
};

// Bulk assign badges to multiple businesses
export const useBulkAssignBadges = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      businessIds, 
      badgeIds 
    }: { 
      businessIds: string[]; 
      badgeIds: string[]; 
    }) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const assignments = businessIds.flatMap(businessId =>
        badgeIds.map(badgeId => ({
          business_id: businessId,
          badge_id: badgeId,
          assigned_at: new Date().toISOString(),
          assigned_by: user.id
        }))
      );

      const { error } = await supabase
        .from('business_badges')
        .upsert(assignments, { 
          onConflict: 'business_id,badge_id',
          ignoreDuplicates: true 
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-badges'] });
      queryClient.invalidateQueries({ queryKey: ['all-badge-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      toast({
        title: "Success",
        description: "Badges assigned successfully",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to assign badges",
      });
    },
  });
};

// Bulk unassign badges from multiple businesses
export const useBulkUnassignBadges = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      businessIds, 
      badgeIds 
    }: { 
      businessIds: string[]; 
      badgeIds: string[]; 
    }) => {
      for (const businessId of businessIds) {
        for (const badgeId of badgeIds) {
          const { error } = await supabase
            .from('business_badges')
            .delete()
            .eq('business_id', businessId)
            .eq('badge_id', badgeId);

          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-badges'] });
      queryClient.invalidateQueries({ queryKey: ['all-badge-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      toast({
        title: "Success",
        description: "Badges unassigned successfully",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to unassign badges",
      });
    },
  });
};

// Get badge assignment statistics
export const useBadgeStats = (communityId: string) => {
  return useQuery({
    queryKey: ['badge-stats', communityId],
    queryFn: async () => {
      if (!communityId) return null;

      // Get total badge assignments count
      const { count: totalAssignments, error: assignmentError } = await supabase
        .from('business_badges')
        .select('*', { count: 'exact', head: true })
        .eq('businesses.community_id', communityId);

      if (assignmentError) throw assignmentError;

      // Get most assigned badges
      const { data: badgeUsage, error: usageError } = await supabase
        .from('business_badges')
        .select(`
          badge_id,
          badges:badge_id (
            name,
            color,
            icon
          )
        `)
        .eq('businesses.community_id', communityId);

      if (usageError) throw usageError;

      // Calculate badge usage statistics
      const badgeUsageMap = badgeUsage?.reduce((acc, item) => {
        if (item.badges) {
          const badgeId = item.badge_id;
          acc[badgeId] = {
            ...item.badges,
            count: (acc[badgeId]?.count || 0) + 1
          };
        }
        return acc;
      }, {} as Record<string, any>) || {};

      const mostAssignedBadges = Object.values(badgeUsageMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      return {
        totalAssignments: totalAssignments || 0,
        mostAssignedBadges
      };
    },
    enabled: !!communityId,
  });
};